import { Label } from "reactstrap";
import { ButtonGroup } from "reactstrap";
import { Button } from "reactstrap";
import { Input } from "reactstrap";
import { FormGroup } from "reactstrap";
import styled from "styled-components";
import ActionIcon from "../../../../components/custom/ActionIcon";
import { useEffect, useState } from "react";
import useTypedForm from "../../../../components/custom/hookForm/useTypedForm";
import { IFile, IRampReport } from "../../../../globals/interfaces";
import useUser from "../../../../customHooks/useUser";
import { formatDatetime, getGuid, getTsDate, notify } from "../../../../utils";
import moment from "moment";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./yupSchema";
import FormError from "../../../../components/custom/FormError2";
import ModalAddRampReport from "./ModalAddRampReport";
import useModal from "../../../../customHooks/useModal";
import useData from "./useData";
import RenderImages, { RenderImage } from "../RenderImages";
import { apiClientEosNative } from "../../../../apiClient";
//import Select from "../../../../components/Select";
import Select, { Creatable } from 'react-select';
import _ from "lodash";
import { Tab } from "..";

export interface IRampReportWithFiles extends IRampReport {
    files: Array<IFile>;
}

export default function RampReport({
    activeTab
}: {
    activeTab: Tab;
}) {

    const { user } = useUser();
    const { s_unit } = user;
    const { modal, setModal } = useModal();

    const defaultData = {
        'createdBy': user.s_email,
        'createdAt': getTsDate(),
        'unit': s_unit,
        'collectionId': getGuid(),
        'files': []
    }

    const {
        formState: {
            errors,
            isValid
        },
        handleSubmit,
        reset,
        trigger,
        setValue,
        watch,
        FormInput
    } = useTypedForm<IRampReportWithFiles>({
        mode: 'all',
        resolver: yupResolver(schema)
    });

    const mawb = watch('mawb');
    const files = watch('files');
    const flightNumber = watch('flightNumber');
    const flightDate = watch('flightDate');
    const type = watch('type');
    const enableAdd =
        activeTab === 'RAMP' && 
        Boolean(flightNumber) && 
        flightNumber.length >= 3 &&
        /[a-zA-Z]/.test(flightNumber) &&
        moment(flightDate).isValid() &&
        Boolean(type) && 
        Boolean(s_unit);

    const { flightOptions, data, mutate, isLoading } = useData(enableAdd, flightNumber, flightDate, type, s_unit);
    
    const setFiles = (files: Array<IFile>) => {
        setValue('files', files);
    }

    const handleReset = () => reset(defaultData);

    const submitReport = async () => {
        handleSubmit(async (formData) => {
            try {
                await apiClientEosNative.post(`/ramp-report`, formData);
                reset({
                    ...defaultData,
                    flightNumber,
                    flightDate,
                    type
                })
                mutate();    
            } catch (err) {
                notify(JSON.stringify(err), 'error');
            }
        })();
    }

    const deleteImage = async (url: string) => {
        try {
            const item = data.find(item => item.fileLink === url);
            if (!item) {
                notify('Error deleting image', 'error');
            }
            const res = await apiClientEosNative.delete(`/ramp-report?guid=${item.guid}&fileLink=${item.fileLink}`);
            setModal(false);
            notify('Image deleted');
            mutate();
        } catch (err) {
            alert(err);
        }
    }

    useEffect(() => {
        handleReset();
        trigger();
    }, []);

    return (
        <Container>
            <h6>Type in Flight. Select Date and Type (IMPORT/EXPORT). Press + to add a photo.</h6>
            <HeaderContainer>
                <FlexContainer>
                    <FormInput 
                        name={'flightDate'}
                        label={'Flight Date'}
                        type={'date'}
                    />
                    <FormGroup>
                        <Label className={'d-block'}>Flight</Label>
                        <Creatable 
                            options={flightOptions}
                            onChange={option => setValue('flightNumber', _.get(option, 'value', '').toUpperCase())}
                            value={{
                                label: flightNumber,
                                value: flightNumber
                            }}
                            styles={customStyles}
                            className={!moment(flightDate).isValid() && 'custom-disabled'}
                        />
                    </FormGroup>
                    <FormGroup>
                        <div>
                            <Label>Type</Label>
                            <FormError
                                errors={errors}
                                name={'type'}
                            />
                        </div>
                        <ButtonGroup>
                            {['IMPORT', 'EXPORT'].map(_type => (
                                <Button 
                                    onClick={() => setValue('type', _type, {
                                        shouldValidate: true
                                    })}
                                    active={type === _type}
                                >
                                    {_type}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </FormGroup>
                </FlexContainer>
                <ActionIcon 
                    type={'add'}
                    onClick={() => setModal(true)}
                    disabled={!enableAdd}
                />
            </HeaderContainer>
            <RenderSearchResults
                searchComplete={enableAdd && !isLoading} 
                data={data}
                deleteImage={deleteImage}
            />
            <ModalAddRampReport 
                modal={modal}
                setModal={setModal}
                files={files}
                setFiles={setFiles}
                submitReport={submitReport}
            />
        </Container>
    );
}

const RenderSearchResults = ({
    searchComplete,
    data,
    deleteImage
}: {
    searchComplete: boolean;
    data: Array<IRampReport>;
    deleteImage: (url?: string) => Promise<void>;
}) => {

    const images: Array<RenderImage> = data.map(item => ({
        url: item.fileLink,
        title: item.mawb,
        comments: item.comments,
        collectionId: item.collectionId,
        createdBy: item.createdBy,
        createdAt: formatDatetime(item.createdAt)
    }));

    const {
        createdBy,
        createdAt,
        comments,
        mawb,
        flightNumber,
        flightDate,
        type
    } = data?.[0] || {};

    const title = `${flightNumber}/${flightDate}`;

    return (
        <RenderImages
            searchComplete={searchComplete}
            images={images}
            createdBy={createdBy}
            createdAt={formatDatetime(createdAt)}
            comments={comments}
            reportType={'RAMP'}
            title={title}
            deleteImage={deleteImage}
            flightNumber={flightNumber}
            flightDate={flightDate}
            type={type}
        />
    );

}

const Container = styled.div`
    
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const FlexContainer = styled.div`
    display: flex;
    gap: 10px;
`;

const CustomInput = styled(Input)`
    width: 250px;
`;

const customStyles = {
    container: (provided) => ({
        ...provided,
        width: '200px' // Use the width prop if provided, otherwise default to 100%
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999, // Set a high z-index value to ensure it is on top
    }),
    menuPortal: (base) => ({ 
        ...base, 
        zIndex: 9999 // Ensures the menu portal has the highest priority
    })
};